import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { COMPANIES } from "@/data/companies";

export default function Companies() {
  return (
    <div>
      <section className="bg-slate-50 pt-32 md:pt-44 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-widest text-blue-600">
              Our Companies
            </p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight max-w-3xl">
              Specialized businesses, one shared standard
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              Explore the companies owned and operated by AMS Venture Holdings
              Inc., each serving Alberta with dedicated expertise.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 space-y-24 md:space-y-32">
        {COMPANIES.map((c, i) => (
          <Reveal key={c.id}>
            <div
              data-testid={`company-section-${c.id}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-[0_20px_60px_rgb(0,0,0,0.06)]">
                <img
                  src={c.image}
                  alt={c.name}
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                  {c.name}
                </h2>
                <p className="mt-2 text-blue-600 font-medium">{c.tagline}</p>
                <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                  {c.description}
                </p>
                <ul className="mt-7 space-y-3">
                  {c.services.map((s) => (
                    <li key={s} className="flex items-center gap-3">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-50">
                        <Check className="h-3.5 w-3.5 text-blue-600" />
                      </span>
                      <span className="text-slate-800">{s}</span>
                    </li>
                  ))}
                </ul>
                {c.external ? (
                  <Button
                    asChild
                    data-testid={`company-cta-${c.id}`}
                    className="mt-8 rounded-full bg-slate-900 hover:bg-blue-600 text-white transition-colors duration-300"
                  >
                    <a href={c.url}>
                      Visit Nova SecureNet
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                ) : (
                  <Button
                    asChild
                    data-testid={`company-cta-${c.id}`}
                    variant="outline"
                    className="mt-8 rounded-full border-slate-300 hover:border-slate-900 transition-colors duration-300"
                  >
                    <Link to="/contact">
                      Enquire about {c.name.split(" ")[0]}{" "}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-slate-900 py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white">
              Not sure which service you need?
            </h2>
            <p className="mt-4 text-lg text-slate-300 max-w-xl mx-auto">
              Reach out and we&apos;ll point you to the right team.
            </p>
            <Button
              asChild
              data-testid="companies-contact-cta"
              className="mt-8 h-12 rounded-full bg-white text-slate-900 hover:bg-blue-600 hover:text-white px-8 text-base transition-colors duration-300"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
